import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FirstMonthOfferBanner } from "@/components/FirstMonthOffer";

const PILLARS = ["Source", "Energy", "Frequency", "Timelines", "Consciousness"];

const LAWS = [
  { n: "01", title: "The Law of Source", body: "Everything begins within consciousness." },
  { n: "02", title: "The Law of Frequency", body: "What you repeatedly embody influences what you repeatedly experience." },
  { n: "03", title: "The Law of Alignment", body: "Your external life begins changing when your internal direction changes." },
  { n: "04", title: "The Law of Release", body: "You cannot enter a new chapter while continuously protecting the old one." },
  { n: "05", title: "The Law of Identity", body: "Your reality is influenced by the identity from which you make decisions." },
  { n: "06", title: "The Law of Timeline", body: "Every decision creates a different direction for your life." },
  { n: "07", title: "The Law of Becoming", body: "You don't simply manifest something — you become the person capable of living it." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] sm:min-h-[92vh] flex items-center overflow-hidden aurora grain">
        <div className="absolute inset-0 starfield opacity-40 dark:opacity-70 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-6 pt-24 pb-14 sm:pb-16 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent/40 text-accent font-mono text-[10px] tracking-[0.32em] uppercase">
                <Sparkles className="h-3 w-3" /> A digital philosophy
              </span>
            </div>
            <h1 className="font-serif font-light text-[2.5rem] leading-[1.05] sm:text-6xl lg:text-7xl tracking-tight">
              You are not stuck.
              <br />
              <span className="italic text-accent">You are shifting.</span>
            </h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              What if the life you're looking for isn't somewhere in the future?
              What if you're becoming the version of yourself that can experience it?
            </p>
            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link to="/shift-reader" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto btn-pill font-mono tracking-[0.24em] uppercase text-xs h-12 px-8 gap-2"
                  data-testid="hero-cta-discover-btn"
                >
                  Discover your shift
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link
                to="/library"
                className="font-mono text-xs uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground border-b border-transparent hover:border-current pb-1"
                data-testid="hero-library-link"
              >
                Read the teachings →
              </Link>
            </div>
            <div className="mt-12 sm:mt-16 flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-3">
              {PILLARS.map((p, i) => (
                <motion.span
                  key={p}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 + i * 0.08 }}
                  className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.28em] sm:tracking-[0.32em] text-muted-foreground flex items-center gap-4 sm:gap-6"
                >
                  {p}
                  {i < PILLARS.length - 1 && <span className="opacity-40 hidden sm:inline">◆</span>}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* BIG IDEA */}
      <section className="mx-auto max-w-5xl px-5 sm:px-6 py-16 sm:py-24">
        <div className="eyebrow mb-6">The Big Idea</div>
        <p className="font-serif text-xl sm:text-4xl leading-[1.3] sm:leading-[1.25] tracking-tight">
          You don't change your reality by chasing a different life.
          You change your relationship with{" "}
          <span className="italic text-accent">yourself</span>, your choices, your attention and your actions —
          and your life begins to change with you.
        </p>
      </section>

      {/* 7 LAWS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-12 sm:py-16" data-testid="seven-laws-section">
        <div className="mb-10 sm:mb-12 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-3">The framework</div>
            <h2 className="font-serif text-3xl sm:text-5xl leading-tight max-w-xl">
              The 7 Laws of the Shift
            </h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            Seven principles that describe how becoming actually happens — beneath the noise, beneath the goal-setting, beneath the performance.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/40">
          {LAWS.map((l) => (
            <motion.div
              key={l.n}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5 }}
              className="bg-background p-6 sm:p-8 group hover:bg-secondary/40 transition-colors"
              data-testid={`law-card-${l.n}`}
            >
              <div className="font-mono text-xs tracking-widest text-accent mb-4">
                {l.n}
              </div>
              <h3 className="font-serif text-xl sm:text-2xl mb-3 leading-snug">{l.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{l.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-16 sm:py-24">
        <div className="border border-border rounded-sm p-6 sm:p-16 relative overflow-hidden aurora-light dark:aurora grain">
          <div className="relative max-w-2xl">
            <div className="eyebrow mb-4">The viral instrument</div>
            <h2 className="font-serif text-3xl sm:text-5xl leading-tight mb-5 sm:mb-6">
              Take the Shift Reader™
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
              Seven questions. One personalized profile. A mirror for the version of you that's already emerging.
            </p>
            <Link to="/shift-reader" className="block sm:inline-block">
              <Button
                size="lg"
                className="w-full sm:w-auto btn-pill font-mono tracking-[0.24em] uppercase text-xs gap-2"
                data-testid="cta-strip-btn"
              >
                Begin the reading <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <FirstMonthOfferBanner />

      {/* ENTRY PRODUCT TEASER */}
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-4 sm:py-8" data-testid="home-entry-product-section">
        <Link
          to="/membership"
          className="group block border border-border rounded-sm p-6 sm:p-10 hover:border-accent/60 transition-colors relative overflow-hidden"
          data-testid="home-entry-product-link"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 items-center">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="eyebrow">Start the shift</span>
                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-accent border border-accent/40 px-2 py-1 rounded-full">
                  Entry • $33
                </span>
              </div>
              <h3 className="font-serif text-2xl sm:text-4xl leading-tight mb-3">
                7-Day Frequency Reset
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-xl">
                Seven daily transmissions and frequency journal prompts —
                the simplest way to feel a real shift in one week.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.24em] text-muted-foreground/80">
                <span>7 Days</span>
                <span className="opacity-40">◆</span>
                <span>Daily Prompts</span>
                <span className="opacity-40">◆</span>
                <span>Lifetime Access</span>
              </div>
            </div>
            <div className="md:col-span-1 flex md:justify-end">
              <span className="inline-flex items-center gap-2 font-mono tracking-[0.24em] uppercase text-xs text-accent group-hover:gap-3 transition-all">
                See all tiers <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* COMMUNITY */}
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-16 sm:py-24" data-testid="home-community-section">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12 items-center">
          <div className="md:col-span-3">
            <div className="eyebrow mb-4">The community</div>
            <h2 className="font-serif text-3xl sm:text-5xl leading-tight mb-5 sm:mb-6">
              You were never meant to walk
              <br />
              <span className="italic text-accent">the journey alone.</span>
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 max-w-xl">
              Connect with people who are questioning, healing, growing and discovering a
              deeper version of themselves.
            </p>
            <Link to="/community" className="block sm:inline-block">
              <Button
                size="lg"
                className="w-full sm:w-auto btn-pill font-mono tracking-[0.24em] uppercase text-xs gap-2"
                data-testid="home-community-btn"
              >
                Join The Shift <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="md:col-span-2">
            <div className="border border-border p-6 sm:p-8 space-y-3">
              {["Start Here", "The Shift", "Ask Tay", "Wins & Transformations", "Live Conversations"].map((c) => (
                <div key={c} className="flex items-center gap-3 font-mono text-xs tracking-widest uppercase text-muted-foreground">
                  <span className="text-accent">#</span> {c}
                </div>
              ))}
              <div className="text-xs font-mono tracking-widest uppercase text-muted-foreground/60 pt-2">
                + 6 more channels inside
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
